import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getToken } from "../../utils/tokenStore";

export interface AuditFilters {
  date_from?: string;
  date_to?: string;
  operated_by_role?: string;
  operated_by_username?: string;
  course_name?: string;
  course_type?: string;
  item_type?: string;
  old_status?: string;
  new_status?: string;
}

export interface AuditQueryProps extends AuditFilters {
  page: number;
  page_size: number;
  role: string;
}

const initialState = {
  loading: false,
  downloadLoading: false,
  items: [] as any[],
  totalItemCount: 0,
  totalPageCount: 0,
};

const buildQueryString = (params: Record<string, any>) => {
  const filtered = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== ""
  );
  if (filtered.length === 0) return "";
  return "?" + filtered.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
};

export const getAuditTrail = createAsyncThunk(
  "audit/getAuditTrail",
  async (props: AuditQueryProps, { rejectWithValue }) => {
    try {
      const query = buildQueryString({
        user_id: sessionStorage.getItem("userId"),
        ...props,
      });
      const url = `${process.env.REACT_APP_BASE_API_URL}/audit-trail${query}`;
      const token = getToken();
      const res = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const text = await res.text();
      const response = JSON.parse(text.replace(/:\s*NaN/g, ": null"));
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch audit trail"
      );
    }
  }
);

export const downloadAuditTrail = createAsyncThunk(
  "audit/downloadAuditTrail",
  async (props: AuditQueryProps, { rejectWithValue }) => {
    try {
      const query = buildQueryString({
        user_id: sessionStorage.getItem("userId"),
        ...props,
        download: true,
      });
      const url = `${process.env.REACT_APP_BASE_API_URL}/audit-trail${query}`;
      const token = getToken();
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "audit-trail.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      return true;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to download audit trail"
      );
    }
  }
);

const auditSlice = createSlice({
  name: "audit",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAuditTrail.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAuditTrail.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload?.data?.items || [];
        state.totalItemCount = action.payload?.data?.total_item_count || 0;
        state.totalPageCount = action.payload?.data?.total_page_count || 0;
      })
      .addCase(getAuditTrail.rejected, (state) => {
        state.loading = false;
      })
      .addCase(downloadAuditTrail.pending, (state) => {
        state.downloadLoading = true;
      })
      .addCase(downloadAuditTrail.fulfilled, (state) => {
        state.downloadLoading = false;
      })
      .addCase(downloadAuditTrail.rejected, (state) => {
        state.downloadLoading = false;
      });
  },
});

export default auditSlice.reducer;
